import hash from 'object-hash';
import {from, Observable, of} from 'rxjs';
import {concatMap, defaultIfEmpty, filter, map, toArray} from 'rxjs/operators';
import {FieldRelationKind} from '../../enum/field.relation.kind';
import {FieldTypeKind} from '../../enum/field.type.kind';
import {Metadata} from '../entity/core.entity.metadata';

export class Search {
    public static literalFn: (where: any) => any;
    public customWhere: any[] = [];
    public smartSearch: any;
    public fields = new Map<string, any>();
    public ignoreFields: boolean;
    public readonly md5: string = '';
    private metadata: Metadata;

    constructor(private params?: any, private operator: any = '$or') {
        if (params && Object.keys(params).length) {
            Object.keys(params).forEach((key: string) => {
                if (key === 'smartSearch')
                    this.smartSearch = params[key];
                else if (key === '$op')
                    this.operator = params[key];
                else
                    this.fields.set(key, params[key]);
            });
        }

        if (params)
            this.md5 = hash(params);
    }

    public static make(where: any): Search {
        const search = new Search();

        Object.keys(where).forEach((key: string) => {
            if (key === '$op') {
                search.operator = where[key];
                delete where[key];
            } else if (where[key] instanceof Array && where[key].length == 3 && where[key][0] == '$btw') {
                if (where[key][1] && where[key][2])
                    where[key] = {$between: [where[key][1], where[key][2]]};
                else
                    delete where[key];
            } else if (where[key] && (typeof where[key] === 'string') && where[key].split(',').length > 1) {
                where[key] = {$in: where[key].split(',')};
            }
        });

        search.customWhere = where;
        return search;
    }

    public removeField(field: string) {
        this.fields.delete(field);
    }

    public setMetadata(metadata: Metadata) {
        this.metadata = metadata;
    }

    public fromMetadata(metadata: Metadata, operator?: any): Search {
        const search = new Search(this.params);
        search.metadata = metadata;

        if (operator)
            search.operator = operator;

        return search;
    }

    public buildWhere(): Observable<any> {
        const where: any = this.customWhere || [];

        const hasSmartSearch = this.smartSearch && this.smartSearch.length > 0;
        const hasFields = !!this.fields.size;

        if (!hasFields && !hasSmartSearch)
            return of(where);

        const ignoreIfEmpty = this.fields.get('__ignoreIfEmpty');

        const makeTerm = (key: string, value: any, ignoreIfEmpty: any) => {
            const item: any = {};
            if ((ignoreIfEmpty || (value && value instanceof Object && value.__ignoreIfEmpty)) &&
                (!value || (value && value instanceof Object && !value.data)) && !(value instanceof Array)) {
                return null;
            }


            if (ignoreIfEmpty && (value instanceof Array)) {
                let arrayHasValue = false;
                for (let i = 0; i < value.length; i++) {
                    if (i == 0 && (value[i] == '$btw' || value[i] == '$lte' || value[i] == '$gte' || value[i] == '$gt' || value[i] == '$lt' || value[i] == '$iLike')) {
                        continue;
                    }
                    if (value[i]) {
                        arrayHasValue = true;
                        break;
                    }
                }
                if (!arrayHasValue) {
                    return null;
                }
            }

            if (value && value instanceof Object && value.__ignoreIfEmpty) {
                value = value.data;
            }

            if (value != '' && !isNaN(value)) {
                item[key] = {$eq: value};
            } else {
                if (value instanceof Object && value instanceof Array && !value.length) {
                    // dummy;
                } else if ((value instanceof Object && !(value instanceof Array)) || (value instanceof Array && (!value.length || (value[0] != '$btw' && value[0] != '$lte' && value[0] != '$gte' && value[0] != '$gt' && value[0] != '$lt' && value[0] != '$iLike' && value[0] != '$jsonb'))))
                    item[key] = value;
                else if (typeof value === 'boolean')
                    item[key] = {$eq: value};
                else if (value == '!null') {
                    item[key] = {$ne: null};
                } else if (value == 'null') {
                    item[key] = {$eq: null};
                } else if (value === 'true') {
                    item[key] = {$eq: true};
                } else if (value instanceof Array && value.length && value[0] == '$btw') {
                    item[key] = {$between: value.splice(1, 2)};
                } else if (value instanceof Array && value.length && value[0] == '$lte') {
                    item[key] = {$lte: value.splice(1, 1)[0]};
                } else if (value instanceof Array && value.length && value[0] == '$lt') {
                    item[key] = {$lt: value.splice(1, 1)[0]};
                } else if (value instanceof Array && value.length && value[0] == '$gte') {
                    item[key] = {$gte: value.splice(1, 1)[0]};
                } else if (value instanceof Array && value.length && value[0] == '$gt') {
                    item[key] = {$gt: value.splice(1, 1)[0]};
                } else if (value instanceof Array && value.length && value[0] == '$iLike') {
                    item[key] = {$iLike: value.splice(1, 1)[0]};
                } else if (value instanceof Array && value.length && value[0] == '$jsonb') {
                    const values = value.splice(1, 1);
                    const _item: any = {$and: []};
                    _item.$and.push(Search.literalFn(`${key}->"$.${values[0]}"=${values[1]}`));
                    return _item;
                } else if (value && value.startsWith && value.startsWith('$btw')) {
                    value = value.replace('$btw,', '').split(',');
                    item[key] = {$between: value};
                } else if (value && value.split && (key.startsWith('id') || key.endsWith('Id'))) {
                    const values = value.split(',');
                    if (values.length > 1) {
                        item[key] = {$in: []};
                        values.forEach((singleValue: any) => {
                            item[key].$in.push(singleValue);
                        });
                    } else if (value != '')
                        item[key] = {$eq: value};
                } else {
                    if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value))
                        item[key] = {$eq: `${value}`};
                    else if (value === 'true' || value === 'false')
                        item[key] = {$eq: `${value}`};
                    else if (typeof value == 'string' && (/^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/).test(value))
                        item[key] = {$eq: Date.parse(value)};
                    else {
                        if (typeof value == 'string')
                            item[key] = {$iLike: `%${value.split(' ').join('%')}%`};
                        else
                            item[key] = {$iLike: `%${value}%`};
                    }

                    if (value && value.indexOf(',') >= 0) {
                        item[key] = {$in: []};
                        value.split(',').forEach((singleValue: any) => {
                            item[key].$in.push(singleValue);
                        });
                    }
                }
            }
            return item;
        };

        const buildTerm = (key: string, value: any, where: any, ignoreIfEmpty: any) => {
            if (key == '__customWhere') {
                where.push(Search.literalFn(value));
            } else if (key == '$or' || key == '$and') {
                const _value = value instanceof Array
                    ? value.map((v: string) => typeof v === 'string' ? JSON.parse(v) : v)
                    : typeof value === 'string' ? JSON.parse(value) : value;

                const parse = (_value: any, where: any) => {
                    Object.keys(_value).forEach((_key: string) => {
                        buildTerm(_key, _value[_key], where[key], ignoreIfEmpty);
                    });
                };

                if (_value instanceof Array) {
                    const term: any = {[key]: []};
                    _value.forEach((value1 => {
                        parse(value1, term);
                    }));

                    if (term[key].length)
                        where.push(term);
                } else {
                    const term: any = {[key]: []};
                    parse(_value, term);

                    if (term[key].length)
                        where.push(term);

                    where.push(term);
                }
            } else if (!this.metadata || this.isField(key, this.metadata)) {
                const term = makeTerm(key, value, ignoreIfEmpty);

                if (!!term && !!Object.keys(term).length)
                    where.push(term);
            } else if (this.metadata && this.isRelation(key, this.metadata)) {
                const t: any = {};
                let op: any = '$and';
                let parent = key;

                const termBuilder = (val: any, parentKey: string, level: number = 0) => {
                    if (val == '$or' || val == '$and') {
                        op = val;
                    } else if (level <= 1 && val instanceof Object && !(val instanceof Array)) {
                        Object.keys(val).forEach((_field: any) => {
                            termBuilder(val[_field], `${parentKey}.${_field}`, level + 1);
                        });
                    } else {
                        const term = makeTerm(parentKey, val, ignoreIfEmpty);
                        if (!!term && !!term[parentKey])
                            t[`$${parentKey}$`] = term[parentKey];
                    }
                };

                const relationItem = this.getRelation(key, this.metadata);
                if (relationItem.relation == FieldRelationKind.HAS_MANY)
                    parent = relationItem.text;

                termBuilder(value, parent);

                if (!!Object.keys(t).length)
                    where.push({[op]: t});
            }
        };

        this.fields.forEach((value: any, key: string) => {
            if (this.ignoreFields) {
                if (this.metadata.modelTableName == key) {

                    if (typeof value === 'string')
                        value = JSON.parse(value);

                    Object.keys(value).forEach((_key: string) => {
                        buildTerm(_key, value[_key], where, ignoreIfEmpty);
                    });
                }
            } else
                buildTerm(key, value, where, ignoreIfEmpty);
        });

        if (!hasSmartSearch || (this.metadata && (this.metadata.columnType == FieldTypeKind.VIRTUAL || !this.metadata.modelTypeMetadata)))
            return of((where && where.length ? {[this.operator]: where} : {}));

        return from(this.metadata.modelTypeMetadata)
            .pipe(filter((metadata: Metadata) => metadata.search && metadata.search.smartSearch && !this.fields.has(metadata.columnName)))
            .pipe(map((metadata: Metadata) => {
                const item: any = {};

                switch (metadata.columnType) {
                    case FieldTypeKind.VIRTUAL:
                        break;
                    case FieldTypeKind.MONEY:
                    case FieldTypeKind.LONG:
                    case FieldTypeKind.ENUM:
                        if (!isNaN(this.smartSearch)) {
                            item[metadata.columnName] = {$eq: parseFloat(this.smartSearch)};
                            where.push(item);
                        }
                        break;
                    default:
                        if (isNaN(this.smartSearch)) {
                            item[metadata.columnName] = {$iLike: `%${this.smartSearch}%`};
                            where.push(item);
                        }
                        break;

                }
            }), toArray())
            .pipe(defaultIfEmpty(where))
            .pipe(map(() => (where && where.length ? {[this.operator]: where} : {})));
    }

    public buildGlobalWhere(metadata: Metadata): Observable<any> {
        const $and: any = [];
        const where: any = [];

        return of(where)
            .pipe(concatMap(() => {

                this.fields.forEach((value: any, field: string) => {
                    if (this.isField(field, metadata)) {
                        if (value === 'null')
                            value = null;

                        // todo: essa regra de vazio não deve valer, posso pesquisar por campos que esiverem vazios
                        if (value == undefined || value == '') {
                            // nothing
                        } else if ((!isNaN(value) && value.indexOf('.') == -1) || field.startsWith('id') || field.endsWith('Id')) {
                            if (value.split(',').length > 1)
                                $and.push(`("${metadata.modelTableName + '"."' + field + '"'} in (${value.split(',').map((val: string) => `'${val}'`).join(',')}))`);
                            else
                                $and.push(`("${metadata.modelTableName + '"."' + field + '"'} = '${value}')`);
                        } else if (value.startsWith('$btw')) {
                            value = value.replace('$btw,', '').split(',');

                            if (value.length == 2 && value[0] && value[1])
                                $and.push(`("${metadata.modelTableName + '"."' + field + '"'} between '${value[0]}' and '${value[1]}')`);
                        } else if (value.split(',').length > 1) {
                            $and.push(`("${metadata.modelTableName + '"."' + field + '"'} in (${value}))`);
                        } else if (value.split('-').length >= 3) {
                            $and.push(`("${metadata.modelTableName + '"."' + field + '"'} = '${value}')`);
                        } else if (field == 'active') {
                            $and.push(`("${metadata.modelTableName + '"."' + field + '"'} = '${value}')`);
                        } else
                            $and.push(`("${metadata.modelTableName + '"."' + field + '"'} ILIKE '%${value}%')`);
                    }
                });

                if (!this.smartSearch) {
                    const andWhere$ = $and.length ? `(${$and.join(' AND ')})` : '';
                    return of(`${andWhere$.length ? '(' + andWhere$ + ')' : ''}`);
                }

                return from(metadata.modelTypeMetadata)
                    .pipe(filter((_metadata: Metadata) => _metadata.search && _metadata.search.smartSearch))
                    .pipe(map((_metadata: Metadata) => {
                        if (!this.fields.has(_metadata.columnName)) {
                            let text = '';
                            if (_metadata.text) {
                                text = `"${_metadata.text.split('.').join('"."')}"`;
                            }

                            switch (_metadata.columnType) {
                                case FieldTypeKind.MONEY:
                                case FieldTypeKind.LONG:
                                case FieldTypeKind.ENUM:
                                    if (!isNaN(this.smartSearch) && this.smartSearch.indexOf('.') == -1) {
                                        where.push(`(${_metadata.text ? text : ('"' + metadata.modelTableName + '"."' + _metadata.columnName + '"')} = '${this.smartSearch}')`);
                                    }
                                    break;
                                default:
                                    if (isNaN(this.smartSearch) || this.smartSearch.indexOf('.') != -1) {
                                        if (_metadata.text && _metadata.text.split('.').length > 2) {
                                            const terms = _metadata.text.split('.');
                                            const text = terms.splice(0, terms.length - 1).join('->');
                                            where.push(`("${text}"."${terms.pop()}" ILIKE '%${this.smartSearch}%')`);
                                        } else
                                            where.push(`(${_metadata.text ? text : ('"' + metadata.modelTableName + '"."' + _metadata.columnName + '"')} ILIKE '%${this.smartSearch}%')`);
                                    }
                                    break;

                            }
                        }
                    }), toArray())
                    .pipe(concatMap(() => {
                        if (!where.length)
                            return of('(1=1)');

                        const orWhere$ = `(${where.join(' OR ')})`;
                        const andWhere$ = $and.length ? `(${$and.join(' AND ')})` : '';
                        return of(`(${andWhere$.length ? '(' + andWhere$ + ') AND' : ''} ${orWhere$})`);
                    }));
            }))
            .pipe(map((where: any) => {
                if (!Search.literalFn)
                    return where;

                const _where: any = this.customWhere ? this.customWhere : {};
                _where.$or = Search.literalFn(where);

                return _where;
            }));
    }

    public pushValueToWhere(metadata: Metadata, column: any, where: any[], smartSearch: any) {
        const item: any = {};
        switch (metadata.columnType) {
            case FieldTypeKind.MONEY:
            case FieldTypeKind.LONG:
            case FieldTypeKind.ENUM:
                if (!isNaN(this.smartSearch)) {
                    item[column] = {$eq: smartSearch};
                    where.push(item);
                }
                break;
            default:
                if (isNaN(this.smartSearch)) {
                    item[column] = {$iLike: `%${smartSearch}%`};
                    where.push(item);
                }
                break;

        }
    }

    private hasFieldOfMetadata(): boolean {
        return !!Array.from(this.fields.keys()).filter((key: string) => this.metadata && (this.metadata.name == key || this.metadata.relationName == key)).length;
    }

    private isField(columnName: string, metadata: Metadata): boolean {
        return !!metadata.modelTypeMetadata.filter((_metadata: Metadata) => {
            return (_metadata.columnName == columnName);
        }).length;
    }

    private isRelation(columnName: string, metadata: Metadata): boolean {
        return metadata && !!metadata.modelTypeMetadata.filter((_metadata: Metadata) => {
            return (_metadata.relationName == columnName);
        }).length;
    }

    private getRelation(columnName: string, metadata: Metadata): Metadata {
        return metadata.modelTypeMetadata.find((_metadata: Metadata) => {
            return (_metadata.relationName == columnName);
        });
    }
}
